import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, clinicApi } from '@/lib/api';
import type { AuditAction, AuditLogsPage } from '@/types/api';

const AUDIT_KEY = ['audit-log'] as const;

export interface AuditLogParams {
  actorUserId?: string;
  action?: AuditAction;
  /** ISO timestamp inclusive lower bound. */
  from?: string;
  /** ISO timestamp inclusive upper bound. */
  to?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * Audit log list. The backend gates with requirePermission("audit.read")
 * and rejects staff with 403 — we don't pre-check on the client because the
 * sidebar/route guard already hides the screen from non-admins, so a staff
 * caller hitting this hook indicates a bug worth surfacing as an API error.
 */
export function useAuditLogs(params: AuditLogParams = {}) {
  const { actorUserId, action, from, to, page = 1, pageSize = 25, enabled = true } = params;
  const offset = Math.max(0, (page - 1) * pageSize);
  return useQuery({
    queryKey: [
      ...AUDIT_KEY,
      'list',
      { actorUserId, action, from, to, pageSize, offset },
    ],
    queryFn: async () => {
      const search = new URLSearchParams();
      if (actorUserId) search.set('actorUserId', actorUserId);
      if (action) search.set('action', action);
      if (from) search.set('from', from);
      if (to) search.set('to', to);
      search.set('limit', String(pageSize));
      search.set('offset', String(offset));
      return clinicApi.get<AuditLogsPage>(`audit-logs?${search.toString()}`);
    },
    enabled,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}
